import {
  AvailableServiceGroup,
  PublicServiceItem,
  ServiceSelectionGroup,
  ServiceSelectionOption
} from './subscription-dialog.models';

export class SubscriptionServiceSelectionState {
  subscribeAllServices = true;
  serviceTreeTouched = false;
  serviceSearchQuery = '';
  selectedServiceIds: number[] = [];
  serviceOptions: ServiceSelectionOption[] = [];
  serviceGroups: ServiceSelectionGroup[] = [];
  ungroupedServiceOptions: ServiceSelectionOption[] = [];

  get hasActiveServiceSearch(): boolean {
    return this.serviceSearchQuery.trim().length > 0;
  }

  get filteredServiceGroups(): ServiceSelectionGroup[] {
    const query = this.normalizedServiceSearchQuery;
    if (!query) {
      return this.serviceGroups;
    }

    return this.serviceGroups
      .map((group) => {
        const groupMatches = this.matchesSearch(group.name, query);
        const services = groupMatches
          ? group.services
          : group.services.filter((service) => this.serviceMatchesSearch(service, query));

        if (!services.length) {
          return null;
        }

        return {
          ...group,
          services
        };
      })
      .filter((group): group is ServiceSelectionGroup => !!group);
  }

  get filteredUngroupedServiceOptions(): ServiceSelectionOption[] {
    const query = this.normalizedServiceSearchQuery;
    if (!query) {
      return this.ungroupedServiceOptions;
    }

    return this.ungroupedServiceOptions.filter((service) => this.serviceMatchesSearch(service, query));
  }

  get hasServiceSearchResults(): boolean {
    return this.filteredServiceGroups.length > 0 || this.filteredUngroupedServiceOptions.length > 0;
  }

  initialize(
    services: PublicServiceItem[] = [],
    groupedServices: AvailableServiceGroup[] = [],
    options: {
      subscribeAllServices?: boolean;
      selectedServiceIds?: number[];
    } = {}
  ): void {
    const optionMap = new Map<number, ServiceSelectionOption & { displayOrder: number }>();
    const groupedServiceIds = new Set<number>();
    const subscribeAllServices = options.subscribeAllServices !== false;
    const selectedIds = new Set(
      (options.selectedServiceIds || [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
    const addOption = (
      service: PublicServiceItem | undefined | null
    ): (ServiceSelectionOption & { displayOrder: number }) | null => {
      if (!service) {
        return null;
      }
      const serviceId = Number(service?.serviceId);
      const name = String(service?.name || '').trim();
      if (!serviceId || !name) {
        return null;
      }

      const existing = optionMap.get(serviceId);
      const displayOrder = Number.isFinite(Number(service?.displayOrder))
        ? Number(service?.displayOrder)
        : Number.MAX_SAFE_INTEGER;
      if (existing) {
        if (existing.description && !service?.description) {
          return existing;
        }
        existing.description = String(service?.description || existing.description || '').trim();
        existing.displayOrder = Math.min(existing.displayOrder, displayOrder);
        return existing;
      }

      const created = {
        serviceId,
        name,
        description: String(service?.description || '').trim(),
        checked: subscribeAllServices || selectedIds.has(serviceId),
        displayOrder
      };
      optionMap.set(serviceId, created);
      return created;
    };

    const resolveGroups = (): Array<{
      groupId: number | string;
      name: string;
      displayOrder: number;
      services: PublicServiceItem[];
    }> => {
      if (Array.isArray(groupedServices) && groupedServices.length > 0) {
        return groupedServices
          .map((group, index) => {
            const servicesInGroup = Array.isArray(group?.services) ? group.services : [];
            return {
              groupId: group?.groupId ?? index,
              name: String(group?.name || '').trim(),
              displayOrder: Number.isFinite(Number(group?.displayOrder)) ? Number(group?.displayOrder) : index,
              services: servicesInGroup
            };
          })
          .filter((group) => !!group.name);
      }

      const inferred = new Map<string, {
        groupId: number | string;
        name: string;
        displayOrder: number;
        services: PublicServiceItem[];
      }>();

      (services || []).forEach((service, serviceIndex) => {
        const memberships = Array.isArray(service?.groups) ? service.groups : [];
        if (!memberships.length) {
          return;
        }
        memberships.forEach((group, groupIndex) => {
          const groupId = group?.groupId ?? group?.name ?? groupIndex;
          const key = String(groupId);
          const existing = inferred.get(key);
          const displayOrder = Number.isFinite(Number(group?.displayOrder))
            ? Number(group.displayOrder)
            : serviceIndex;
          if (existing) {
            existing.services.push(service);
            existing.displayOrder = Math.min(existing.displayOrder, displayOrder);
            return;
          }
          inferred.set(key, {
            groupId,
            name: String(group?.name || '').trim(),
            displayOrder,
            services: [service]
          });
        });
      });

      return Array.from(inferred.values()).filter((group) => !!group.name);
    };

    const resolvedGroups = resolveGroups();
    const mappedGroups: ServiceSelectionGroup[] = [];
    const sortedResolvedGroups = [...resolvedGroups].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
    );

    sortedResolvedGroups.forEach((group, index) => {
      const groupServices = group.services
        .map((service) => addOption(service))
        .filter((service): service is ServiceSelectionOption & { displayOrder: number } => !!service);

      if (!groupServices.length) {
        return;
      }

      groupServices.forEach((service) => groupedServiceIds.add(service.serviceId));
      const sortedGroupServices = [...groupServices].sort(
        (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
      );

      mappedGroups.push({
        groupId: group.groupId,
        name: group.name,
        displayOrder: group.displayOrder,
        expanded: index === 0,
        services: sortedGroupServices
      });
    });

    const sortedUngroupedServices = [...(services || [])]
      .map((service) => addOption(service))
      .filter(
        (service): service is ServiceSelectionOption & { displayOrder: number } =>
          !!service && !groupedServiceIds.has(service.serviceId)
      )
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));

    const sortedServiceOptions = [...optionMap.values()].sort(
      (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name)
    );

    this.serviceGroups = mappedGroups;
    this.ungroupedServiceOptions = sortedUngroupedServices;
    this.serviceOptions = sortedServiceOptions;
    this.selectedServiceIds = subscribeAllServices ? [] : this.collectSelectedServiceIds();
    this.serviceTreeTouched = false;
    this.subscribeAllServices = subscribeAllServices;
    this.clearServiceSearch();
  }

  setServiceSearchQuery(value: string): void {
    this.serviceSearchQuery = String(value || '');
  }

  clearServiceSearch(): void {
    this.serviceSearchQuery = '';
  }

  toggleAllServices(value: boolean): void {
    this.subscribeAllServices = value;

    this.serviceOptions.forEach((service) => {
      service.checked = value;
    });
    this.selectedServiceIds = [];
    this.serviceTreeTouched = !value;
  }

  toggleService(service: ServiceSelectionOption, checked: boolean): void {
    if (this.subscribeAllServices && !checked) {
      this.subscribeAllServices = false;
    }
    service.checked = checked;
    this.afterSelectionChanged();
  }

  toggleServiceGroup(group: ServiceSelectionGroup, checked: boolean): void {
    if (!group) {
      return;
    }
    if (this.subscribeAllServices && !checked) {
      this.subscribeAllServices = false;
    }
    group.services.forEach((service) => {
      service.checked = checked;
    });
    this.afterSelectionChanged();
  }

  toggleGroupExpansion(group: ServiceSelectionGroup): void {
    if (!group) {
      return;
    }
    const originalGroup = this.serviceGroups.find((item) =>
      item === group || (item.groupId === group.groupId && item.name === group.name)
    );
    (originalGroup || group).expanded = !(originalGroup || group).expanded;
  }

  isServiceGroupSelected(group: ServiceSelectionGroup): boolean {
    if (this.subscribeAllServices) {
      return true;
    }
    const selectedCount = this.getSelectedServiceCountForGroup(group);
    return selectedCount > 0 && selectedCount === group.services.length;
  }

  isServiceGroupIndeterminate(group: ServiceSelectionGroup): boolean {
    if (this.subscribeAllServices) {
      return false;
    }
    const selectedCount = this.getSelectedServiceCountForGroup(group);
    return selectedCount > 0 && selectedCount < group.services.length;
  }

  isServiceSelected(service: ServiceSelectionOption): boolean {
    return this.subscribeAllServices || service.checked;
  }

  isAllServicesSelected(): boolean {
    if (this.subscribeAllServices) {
      return true;
    }
    return this.serviceOptions.length > 0 && this.selectedServiceIds.length === this.serviceOptions.length;
  }

  isAllServicesIndeterminate(): boolean {
    if (this.subscribeAllServices) {
      return false;
    }
    return this.selectedServiceIds.length > 0 && this.selectedServiceIds.length < this.serviceOptions.length;
  }

  private afterSelectionChanged(): void {
    this.selectedServiceIds = this.collectSelectedServiceIds();
    this.serviceTreeTouched = true;
  }

  private collectSelectedServiceIds(): number[] {
    return this.serviceOptions
      .filter((service) => service.checked)
      .map((service) => service.serviceId);
  }

  private getSelectedServiceCountForGroup(group: ServiceSelectionGroup): number {
    return group.services.filter((service) => service.checked).length;
  }

  private get normalizedServiceSearchQuery(): string {
    return this.serviceSearchQuery.trim().toLowerCase();
  }

  private serviceMatchesSearch(service: ServiceSelectionOption, normalizedSearch: string): boolean {
    return this.matchesSearch(service?.name, normalizedSearch) ||
      this.matchesSearch(service?.description, normalizedSearch);
  }

  private matchesSearch(value: string | undefined, normalizedSearch: string): boolean {
    return String(value || '').trim().toLowerCase().includes(normalizedSearch);
  }
}
